import { ArrowRight, HeartPulse, ShieldCheck, Stethoscope, UsersRound } from "lucide-react";
import { Link } from "react-router-dom";

import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { Button } from "@/components/ui/button";

const services = [
  {
    title: "SEND transport",
    icon: UsersRound,
    description: "Specialist support for children and young people who require consistent, well-managed journeys.",
    delivery: "Delivered through trained drivers, structured routes, safeguarding-aware handovers, and dependable communication.",
    platform: "Guardian gives councils, schools, and families clearer visibility into timing, status, and handover confirmation.",
  },
  {
    title: "School transport",
    icon: ShieldCheck,
    description: "Daily and scheduled school journeys built around punctuality, student wellbeing, and reliable routines.",
    delivery: "Safely delivered through route planning, driver accountability, and operational oversight across every trip.",
    platform: "Schools receive better arrival visibility, issue reporting, and clearer communication when plans change.",
  },
  {
    title: "Vulnerable adult transport",
    icon: HeartPulse,
    description: "Respectful, supported travel for vulnerable adults who benefit from consistency and secure transport processes.",
    delivery: "Delivered with safeguarding awareness, considerate service standards, and accountable status updates.",
    platform: "Operational teams can monitor journeys, record issues, and maintain stronger service continuity across stakeholders.",
  },
  {
    title: "Non-emergency patient transport",
    icon: Stethoscope,
    description: "Planned transport for appointments and care settings, designed to reduce uncertainty and improve coordination.",
    delivery: "Carefully managed through dependable scheduling, clear escalation paths, and service traceability.",
    platform: "Digital oversight supports journey assurance, operational control, and auditable service records.",
  },
];

export const ServicesPage = () => {
  return (
    <div>
      <PageHero
        eyebrow="Services"
        title="Specialist transport services strengthened by digital safeguarding control."
        description="CareFleet delivers specialist transport using wheelchair accessible vehicles (WAVs), providing a controlled, safeguarding-focused environment for passengers requiring additional support. This supports a small-scale, carefully managed transport model with clear operational checkpoints."
        highlights={["Wheelchair accessible vehicles", "Controlled small-scale transport", "SEND support", "Safeguarding-focused journeys"]}
        actions={[
          { label: "Request Demo", to: "/contact", testId: "services-hero-request-demo-button" },
          { label: "View Platform", to: "/platform", testId: "services-hero-platform-button", variant: "outline" },
        ]}
        baseTestId="services-hero"
        visual={
          <div className="image-shell">
            <img
              src="https://images.unsplash.com/photo-1732194439331-08ec61c4df4f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
              alt="Wheelchair accessible van with ramp and passenger support"
              className="aspect-[5/4] w-full object-cover object-center"
            />
          </div>
        }
      />

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="Service lines"
            title="Every service is delivered safely, visibly, and with structured accountability."
            description="We tailor operations to the journey type while keeping a consistent safeguarding and oversight framework across the full service, designed to reduce uncertainty through structured workflows and real-time oversight."
            testId="services-grid-heading"
          />

          <div className="grid gap-6 lg:grid-cols-2">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Reveal key={service.title}>
                  <div className="soft-card h-full p-8" data-testid={`services-card-${service.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-mist text-brand-navy">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h2 className="mt-6 font-heading text-3xl font-semibold text-slate-950">{service.title}</h2>
                    <div className="mt-6 space-y-5 text-base leading-8 text-slate-600">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-teal">What the service is</p>
                        <p className="mt-2">{service.description}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-teal">How it is delivered safely</p>
                        <p className="mt-2">{service.delivery}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-teal">How the platform enhances it</p>
                        <p className="mt-2">{service.platform}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-brand-mist/70 px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-3">
          {[
            "Operational planning built around service reliability and stakeholder clarity.",
            "Safeguarding considerations embedded into handovers, incident handling, and communication.",
            "Digital oversight supporting councils, schools, and families with a clearer service picture.",
          ].map((item) => (
            <Reveal key={item}>
              <div className="soft-card h-full p-8" data-testid={`services-standard-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="font-heading text-2xl font-semibold text-slate-950">Service standard</p>
                <p className="mt-4 text-base leading-8 text-slate-600">{item}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <div className="image-shell" data-testid="services-specialist-vehicle-image-card">
              <img
                src="https://images.unsplash.com/photo-1732194439331-08ec61c4df4f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                alt="Wheelchair accessible vehicle with ramp in a calm transport environment"
                className="aspect-[4/5] w-full object-cover object-center"
              />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="soft-card h-full p-8" data-testid="services-specialist-vehicle-card">
              <p className="eyebrow">Specialist vehicle approach</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold text-slate-950">Specialist vehicle approach</h2>
              <p className="mt-4 text-base leading-8 text-slate-600">
                CareFleet uses wheelchair accessible vehicles (WAVs) to provide a safer, more controlled transport environment.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {[
                  "Better supervision",
                  "Calmer journeys",
                  "Safer handovers",
                  "More appropriate transport for SEND and vulnerable passengers",
                ].map((item) => (
                  <div key={item} className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid={`services-specialist-vehicle-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <p className="font-medium text-slate-800">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-4 pb-24 pt-16 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto flex max-w-7xl flex-col gap-6 rounded-[32px] border border-slate-200 bg-white px-8 py-10 shadow-[0_20px_60px_-35px_rgba(15,23,42,0.32)] lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="eyebrow" data-testid="services-cta-eyebrow">Planning a transport contract or service review?</p>
              <h2 className="mt-3 font-heading text-3xl font-semibold text-slate-950" data-testid="services-cta-title">
                Let’s map the right service model for your passengers and stakeholders.
              </h2>
            </div>
            <Button asChild className="rounded-full bg-brand-navy px-6 py-6 text-white hover:bg-brand-navy/90">
              <Link to="/contact" data-testid="services-cta-request-demo-button">
                Request Demo
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </Reveal>
      </section>
    </div>
  );
};